import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { Params } from "@angular/router";
import { map, switchMap, takeUntil, zip } from "rxjs";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { IAssigneesState, IAssigneesStoreState } from "@gtmhub/assignees";
import { ICurrentEmployeeStoreState, IEmployee } from "@gtmhub/employees";
import { getEmployeeTeamIdsAndManagedTeamIds } from "@gtmhub/home/utils";
import { reduxStoreContainer } from "@gtmhub/state-management/state-management.module";
import { toKeyMap } from "@gtmhub/util";
import { Assignee } from "@webapp/assignees/models/assignee.models";
import { ReduxStoreObserver } from "@webapp/core/state-management/redux-store-observer";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { ModuleFlag } from "@webapp/feature-toggles/models/feature-module.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { PermissionsFacade } from "@webapp/permissions/services/permissions-facade.service";
import { CheckInSettingsDTO } from "@webapp/reflections/models/checkins-facade.models";
import { CheckInsFacade } from "@webapp/reflections/services/checkins-facade.service";
import { sortArrayByProperty } from "./utils/utils";

@Component({
  selector: "user-details",
  templateUrl: "./user-details.component.html",
  styleUrls: ["./user-details.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DestroyService],
})
export class UserDetailsComponent implements OnInit {
  public employee: IEmployee;
  public assigneesState: IAssigneesState;
  public altText: string;
  public uiSrefParams: Params;
  public checkInsEnabled: boolean;
  private checkInsSettingsMap: Record<string, CheckInSettingsDTO>;

  public managedTeams: Assignee[] = [];
  public memberTeams: Assignee[] = [];

  constructor(
    private accountService: AccountService,
    private permissionsFacade: PermissionsFacade,
    private destroyService$: DestroyService,
    private featureTogglesFacade: FeatureTogglesFacade,
    private cdr: ChangeDetectorRef,
    private checkInsFacade: CheckInsFacade
  ) {}

  public ngOnInit(): void {
    const { reduxStore } = reduxStoreContainer;
    const reduxObserver = new ReduxStoreObserver(reduxStore);

    zip([
      reduxObserver.whenFetched$<IAssigneesStoreState & ICurrentEmployeeStoreState>("assignees", "currentEmployee"),
      this.featureTogglesFacade.isFeatureAvailable$(ModuleFlag.Reflections),
      this.permissionsFacade.hasPermission$("AccessPeople"),
    ])
      .pipe(
        takeUntil(this.destroyService$),
        switchMap(([storeData, checkInsEnabled, hasPermissions]) => {
          const userBelongsToTeamIds = getEmployeeTeamIdsAndManagedTeamIds(storeData.currentEmployee.info);

          const updatesFilter = {
            filter: { teamId: { $in: userBelongsToTeamIds } },
          };

          return this.checkInsFacade.getCheckInSettings$(updatesFilter).pipe(map((checkInSettings) => ({ storeData, checkInsEnabled, hasPermissions, checkInSettings })));
        })
      )
      .subscribe(({ storeData, checkInsEnabled, hasPermissions, checkInSettings }) => {
        if (hasPermissions && this.accountService.featureAvailable("people.teams")) {
          this.employee = storeData.currentEmployee.info;
          this.assigneesState = storeData.assignees;

          this.checkInsEnabled = checkInsEnabled;
          this.altText = "Go to your profile";
          this.uiSrefParams = { employeeId: this.employee.id };

          if (checkInsEnabled) {
            this.checkInsSettingsMap = toKeyMap("teamId", checkInSettings.items);
          }

          const managed = this.employee.managedTeamIds?.map((element) => this.assigneesState.map[element]).filter((team) => team && team.isActive);
          this.managedTeams = sortArrayByProperty(managed, "name");

          const memberOf = this.employee.teamIds?.map((element) => this.assigneesState.map[element]).filter((team) => team && team.isActive);
          this.memberTeams = sortArrayByProperty(memberOf, "name");

          this.cdr.detectChanges();
        }
      });
  }

  public activeTab(teamId: string): string {
    let activeTab = "";
    if (this.checkInsEnabled && this.checkInsSettingsMap[teamId].reflectionsEnabled) {
      activeTab = "check-ins";
    }
    return activeTab;
  }
}
